<template>
    <div
        :class="['sticky-container', { 'is-sticky': isSticky && !disabled, sticky: !disabled }]"
        :style="{ [position]: offsetPx }"
    >
        <slot></slot>
    </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { defineComponent, ref } from 'vue';

type StickyPosition = 'top' | 'bottom';

/**
 * This component toggles a class `is-sticky` on the element when it is sticky.
 * This might be helpful for styling.
 *
 * Read https://medium.com/@elad/css-position-sticky-how-it-really-works-54cd01dc2d46 when it doesn't seem to work.
 */
export default defineComponent({
    name: 'StickyContainer',
    props: {
        /**
         * The offset in px from top/bottom of the viewport.
         */
        offset: {
            type: Number, // in px
            required: true,
        },
        /**
         * The position of the sticky element.
         */
        position: {
            type: String as PropType<StickyPosition>,
            default: 'top',
            validator: (value: string) => ['top', 'bottom'].includes(value),
        },
        /**
         * Disables the sticky behavior.
         */
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['sticky'],
    setup() {
        return {
            isSticky: ref(false),
            observer: null as IntersectionObserver | null,
        };
    },
    computed: {
        offsetPx() {
            return `${this.offset}px`;
        },
        rootMargin() {
            const offsetPx = `-${this.offset + 1}px`;
            return `${this.position === 'top' ? offsetPx : '100%'} 0px ${
                this.position === 'bottom' ? offsetPx : '100%'
            } 0px`;
        },
    },
    watch: {
        rootMargin() {
            this.initIntersectionObserver();
        },
        disabled(bool) {
            if (bool) {
                this.cleanup();
            } else {
                this.initIntersectionObserver();
            }
        },
        isSticky: {
            immediate: true,
            handler(bool) {
                this.$emit('sticky', bool);
            },
        },
    },
    mounted() {
        this.initIntersectionObserver();
    },
    beforeUnmount() {
        this.cleanup();
    },
    methods: {
        initIntersectionObserver() {
            if (this.disabled) return;
            this.observer?.unobserve(this.$el);

            /**
             * In order to know if a sticky element is sticky, we tailor the intersection margin in a way that it creates an intersection of 1px where the element would be sticky.
             * That way, we have an efficient way to receive a callback from the browser once the element is in the intended position.
             */
            this.observer = new IntersectionObserver(
                ([entry]) => {
                    this.isSticky =
                        entry.intersectionRatio < 1 &&
                        entry.intersectionRect[this.position] === entry.rootBounds?.[this.position];
                },
                {
                    threshold: [0, 1],
                    root: document,
                    rootMargin: this.rootMargin,
                },
            );
            this.observer.observe(this.$el);
        },
        cleanup() {
            this.observer?.unobserve(this.$el);
            this.observer?.disconnect();
        },
    },
});
</script>
